import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  Pagination,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { contractorAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const ContractorMyLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const loadLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await contractorAPI.getMyLeads({ page, limit: 30 });
      setLeads(response.data.data);
      setTotalPages(response.data.last_page);
      setTotal(response.data.total);
    } catch {
      addToast('Fehler beim Laden der Leads', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, addToast]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const getDirectionLabel = (dir: string) => {
    if (dir === 'inbound') return 'Inbound';
    if (dir === 'outbound') return 'Outbound';
    if (dir === 'both') return 'Inbound & Outbound';
    return dir || '-';
  };

  return (
    <PortalLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Meine Leads
          </Typography>
          <Typography color="textSecondary" variant="body2">
            {total} gekaufte Leads
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : leads.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0' }}>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            Sie haben noch keine Leads erworben.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/contractor/browse-jobs')}
            sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
          >
            Ausschreibungen durchsuchen
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f6fa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#555', width: 70 }}>Nr.</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Erworben am</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Firma</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>PLZ</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Stadt</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Inbound/Outbound</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }}>Angesehen</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#555' }} align="right">Ansehen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow
                    key={lead.lead_id || lead.id}
                    hover
                    sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                      #{lead.id}
                    </TableCell>
                    <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                      {formatDate(lead.purchased_at)}
                    </TableCell>
                    <TableCell sx={{ color: '#333', fontSize: '0.85rem' }}>
                      {lead.company_name || lead.contact_name || '-'}
                    </TableCell>
                    <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                      {lead.zip_code || '-'}
                    </TableCell>
                    <TableCell sx={{ color: '#555', fontSize: '0.85rem' }}>
                      {lead.city || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getDirectionLabel(lead.direction)}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.72rem',
                          borderColor: lead.direction === 'inbound' ? '#E85D26' : '#533483',
                          color: lead.direction === 'inbound' ? '#E85D26' : '#533483',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {lead.viewed_at ? (
                        <Chip label="Ja" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ) : (
                        <Chip label="Nein" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => navigate(`/contractor/inquiry/${lead.id}`)}
                        sx={{ color: '#E85D26', fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        Details anzeigen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </PortalLayout>
  );
};

export default ContractorMyLeadsPage;
